/**
 * Unified hardcoded category tree for all stores.
 * CLAW-426
 * 
 * Since 93/95 categories in Medusa have parent_category_id = null,
 * we define the hierarchy here. This is the single source of truth
 * for breadcrumbs, sidebar trees, and navigation across all stores.
 * 
 * IMPORTANT: handles must match ACTUAL Medusa category handles from the DB.
 * Chokyo categories mostly have "chokyo-" prefix, oriental ones don't.
 * A few chokyo categories (cat, dog, pee-toilet, dry-food, etc.) lack the prefix.
 */

export interface CategoryTreeNode {
  handle: string
  label: string
  children?: CategoryTreeNode[]
}

// ─── Fun Store / PetSelect (source: chokyo) ───────────────────────

const FUN_STORE_TREE: CategoryTreeNode[] = [
  {
    handle: "cat",
    label: "Cat",
    children: [
      { handle: "dry-food", label: "Dry Food" },
      { handle: "cat-wet-food", label: "Wet Food" },
      { handle: "inaba-ciao", label: "Inaba/Ciao" },
      { handle: "cat-treats", label: "Cat Treats" },
      {
        handle: "litter-accessories",
        label: "Litter & Accessories",
        children: [
          { handle: "cat-litter", label: "Cat Litter" },
          { handle: "cat-litter-boxes-accessories", label: "Cat Litter Boxes & Accessories" },
          { handle: "cat-litter-box", label: "Cat Litter Box" },
        ],
      },
      {
        handle: "cat-furniture",
        label: "Cat Furniture",
        children: [
          { handle: "cat-tree", label: "Cat Trees & Towers" },
          { handle: "-cat-scratchers-collection-stylish-comfort-for-every-feline", label: "Scratchers" },
        ],
      },
      {
        handle: "feeding",
        label: "Feeding & Bowls",
        children: [
          { handle: "-slow-feeder-bowls-travel-feeder-bowls", label: "Slow Feeder & Travel Bowls" },
        ],
      },
      {
        handle: "carrier",
        label: "Travel & Carriers",
        children: [
          { handle: "trolleys-strollers", label: "Trolleys / Strollers" },
        ],
      },
      { handle: "interactive-puzzle-toy-for-dogs-cats-press-rotate-reward-play", label: "Interactive Puzzle Toys" },
      { handle: "-cat-teasers-collection-interactive-playtime-for-happy-cats", label: "Wand Toys & Teasers" },
      { handle: "bath-cleaning-gentle-care-for-every-whisker-and-paw-", label: "Bath & Cleaning" },
      { handle: "brush-deshedding", label: "Brush & Deshedding" },
      { handle: "leash-clothing", label: "Leash & Clothing" },
      { handle: "bedding-comfort", label: "Bedding & Comfort" },
    ],
  },
  {
    handle: "dog",
    label: "Dog",
    children: [
      { handle: "dog-dry-food", label: "Dog Dry Food" },
      { handle: "dog-wet-food", label: "Dog Wet Food" },
      { handle: "fresh-dog-food", label: "Fresh Dog Food" },
      { handle: "freeze-dried-treats", label: "Freeze Dried Treats" },
      { handle: "dog-treats", label: "Dog Treats" },
      { handle: "dental-chews", label: "Dental Chews" },
      {
        handle: "pee-toilet",
        label: "Pee & Toilet",
        children: [
          { handle: "indoor-dog-toilets-grids-grass-pads", label: "Indoor Dog Toilets / Grids / Grass Pads" },
          { handle: "cleaning-deodorizer", label: "Cleaning & Deodorizer" },
        ],
      },
      {
        handle: "carrier",
        label: "Travel & Carriers",
        children: [
          { handle: "trolleys-strollers", label: "Trolleys / Strollers" },
        ],
      },
      { handle: "leash-clothing", label: "Leash & Clothing" },
      { handle: "interactive-puzzle-toy-for-dogs-cats-press-rotate-reward-play", label: "Interactive Puzzle Toys" },
      { handle: "bath-cleaning-gentle-care-for-every-whisker-and-paw-", label: "Bath & Cleaning" },
      { handle: "brush-deshedding", label: "Brush & Deshedding" },
      {
        handle: "bedding-comfort",
        label: "Bedding & Comfort",
        children: [
          { handle: "bedding", label: "Bedding" },
        ],
      },
      {
        handle: "feeding",
        label: "Feeding & Bowls",
        children: [
          { handle: "-slow-feeder-bowls-travel-feeder-bowls", label: "Slow Feeder & Travel Bowls" },
        ],
      },
    ],
  },
  {
    handle: "small-animals",
    label: "Small Animals",
    children: [
      { handle: "-small-animals-food-treats-nutrition-made-simple-natural", label: "Food & Treats" },
      { handle: "-small-animal-toys-keep-little-paws-busy-happy", label: "Activity Toys" },
      { handle: "small-animals-bedding-housing", label: "Bedding & Housing" },
    ],
  },
]

// ─── Annie Time & Aussie Style (source: oriental) ─────────────────

const ORIENTAL_STORE_TREE: CategoryTreeNode[] = [
  {
    handle: "jewellery",
    label: "Jewellery",
    children: [
      {
        handle: "earrings",
        label: "Earrings",
        children: [
          { handle: "drop-earrings", label: "Drop Earrings" },
          { handle: "stud-earrings", label: "Stud Earrings" },
          { handle: "earring-clips", label: "Earring Clips" },
          { handle: "28-earrings", label: "$28 Earrings" },
          { handle: "38-earings", label: "$38 Earrings" },
          { handle: "48-earrings", label: "$48 Earrings" },
          { handle: "68-earrings", label: "$68 Earrings" },
        ],
      },
      {
        handle: "bracelet",
        label: "Bracelets",
        children: [
          { handle: "68-bracelets", label: "$68 Bracelets" },
          { handle: "78-bracelet", label: "$78 Bracelets" },
          { handle: "128-bracelets", label: "$128 Bracelets" },
        ],
      },
      { handle: "necklaces", label: "Necklaces" },
      { handle: "rings-1", label: "Rings" },
      { handle: "hairpin", label: "Hairpins" },
      {
        handle: "chakra-collection",
        label: "Chakra Collection",
        children: [
          { handle: "root-chakra", label: "Root Chakra" },
          { handle: "sacral-chakra", label: "Sacral Chakra" },
          { handle: "solar-plexus-chakra", label: "Solar Plexus Chakra" },
          { handle: "heart-chakra", label: "Heart Chakra" },
          { handle: "throat-chakra", label: "Throat Chakra" },
          { handle: "third-eye-chakra", label: "Third Eye Chakra" },
          { handle: "crown-chakra", label: "Crown Chakra" },
        ],
      },
      {
        handle: "collections",
        label: "Collections",
        children: [
          { handle: "dragon-collection", label: "Dragon Collection" },
          { handle: "lotus-collection", label: "Lotus Collection" },
          { handle: "orient-collection", label: "Orient Collection" },
          { handle: "palace-collection", label: "Palace Collection" },
          { handle: "serene-collection", label: "Serene Collection" },
          { handle: "eternal-collection", label: "Eternal Collection" },
        ],
      },
      { handle: "red-series", label: "Red Series" },
      { handle: "yellow-series", label: "Yellow Series" },
      { handle: "white-series", label: "White Series" },
      { handle: "black-series", label: "Black Series" },
      { handle: "green-series", label: "Green Series" },
      { handle: "cyan-series", label: "Cyan Series" },
      { handle: "purple-series", label: "Purple Series" },
    ],
  },
  {
    handle: "clothing-vip",
    label: "Clothing",
    children: [
      { handle: "jackets", label: "Jackets" },
      { handle: "vests", label: "Vests" },
      { handle: "tank-tops", label: "Tank Tops" },
      { handle: "pants", label: "Pants" },
      { handle: "skirts", label: "Skirts" },
    ],
  },
  {
    handle: "silk-scarf",
    label: "Silk Scarves",
    children: [
      { handle: "50-x-50", label: "50×50" },
      { handle: "90-x-90", label: "90×90" },
      { handle: "135-131", label: "135×131" },
    ],
  },
  { handle: "bags", label: "Bags" },
  { handle: "limited-edition-set", label: "Limited Edition Sets" },
  { handle: "promotion", label: "Promotion" },
]

// ─── Store → Tree mapping ──────────────────────────────────────────

const STORE_TREES: Record<string, CategoryTreeNode[]> = {
  fun: FUN_STORE_TREE,
  chokyo: FUN_STORE_TREE,
  annietime: ORIENTAL_STORE_TREE,
  aussiestyle: ORIENTAL_STORE_TREE,
  oriental: ORIENTAL_STORE_TREE,
}

/**
 * Get the category tree for a store.
 */
export function getCategoryTree(storeName: string): CategoryTreeNode[] {
  return STORE_TREES[storeName] || []
}

/**
 * Build a handle→node map from the tree.
 */
function buildNodeMap(
  nodes: CategoryTreeNode[],
  map: Map<string, CategoryTreeNode> = new Map()
): Map<string, CategoryTreeNode> {
  for (const node of nodes) {
    map.set(node.handle, node)
    if (node.children) {
      buildNodeMap(node.children, map)
    }
  }
  return map
}

/**
 * Build a handle→parentHandle map from the tree.
 */
function buildParentMap(
  nodes: CategoryTreeNode[],
  parentHandle: string | null = null,
  map: Map<string, string | null> = new Map()
): Map<string, string | null> {
  for (const node of nodes) {
    map.set(node.handle, parentHandle)
    if (node.children) {
      buildParentMap(node.children, node.handle, map)
    }
  }
  return map
}

export interface BreadcrumbSegment {
  label: string
  handle: string
}

/**
 * Get the full breadcrumb path for a category handle within a store's tree.
 * Returns root → leaf (e.g. [Dog, Pee & Toilet, Indoor Dog Toilets]).
 * Returns empty array if handle not found.
 */
export function getBreadcrumbPath(
  categoryHandle: string,
  storeName: string
): BreadcrumbSegment[] {
  const tree = getCategoryTree(storeName)
  if (!tree.length) return []

  const nodeMap = buildNodeMap(tree)
  const parentMap = buildParentMap(tree)

  if (!nodeMap.has(categoryHandle)) return []

  const path: BreadcrumbSegment[] = []
  let current: string | null = categoryHandle

  while (current) {
    const node = nodeMap.get(current)
    if (!node) break
    path.unshift({ label: node.label, handle: node.handle })
    current = parentMap.get(current) || null
  }

  return path
}

/**
 * Find a node and its children in the tree by handle.
 */
export function findCategoryNode(
  categoryHandle: string,
  storeName: string
): CategoryTreeNode | null {
  const tree = getCategoryTree(storeName)
  const nodeMap = buildNodeMap(tree)
  return nodeMap.get(categoryHandle) || null
}

/**
 * Get siblings (other children of the same parent) for a category handle.
 */
export function getCategorySiblings(
  categoryHandle: string,
  storeName: string
): { parent: CategoryTreeNode | null; siblings: CategoryTreeNode[] } {
  const tree = getCategoryTree(storeName)
  const parentMap = buildParentMap(tree)
  const nodeMap = buildNodeMap(tree)

  const parentHandle = parentMap.get(categoryHandle)
  if (parentHandle === undefined) {
    // Not found in tree
    return { parent: null, siblings: [] }
  }
  if (parentHandle === null) {
    // Top-level node; siblings are other top-level nodes
    return { parent: null, siblings: tree }
  }

  const parentNode = nodeMap.get(parentHandle)
  if (!parentNode || !parentNode.children) {
    return { parent: parentNode || null, siblings: [] }
  }

  return { parent: parentNode, siblings: parentNode.children }
}
